import React, { useEffect, useState, useMemo } from "react";
import { useParams, useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import {
  GetMachineDetailByPrimaryKey,
  GetAllKpiData,
  GetMachineRunStatus,
  GetMachineUsageTrend,
  GetMachineEarningTrend,
  GetMachineResourceUsage,
  GetMachineVersion,
} from "../../services/ReduxTookit/Slice/MachineRedux";
import { commonservices } from "../../services/CommonServices";
import moment from "moment";
import { mockMachine360Data } from "./machine360MockData";
import { Machine360Header } from "./Machine360Header";
import { Machine360Sidebar } from "./Machine360Sidebar";
import { Machine360KpiCards } from "./Machine360KpiCards";
import { RunStatusChart } from "./RunStatusChart";
import { UsageTrendChart } from "./UsageTrendChart";
import { CostTrendChart } from "./CostTrendChart";
import { ResourceUsageSection } from "./ResourceUsageSection";
import Nodata from "../common-component/Nodata";
import "./Machine360Analytics.css";

const formatTrendDates = (startDateStr, endDateStr, days) => {
  const allDates = [];
  const curr = moment(startDateStr);
  const end = moment(endDateStr);
  while (curr.isSameOrBefore(end)) {
    allDates.push(curr.format("YYYY-MM-DD"));
    curr.add(1, "days");
  }

  const step = days === 7 ? 1 : days === 30 ? 3 : 10;
  const labels = allDates.map((dStr, idx) => {
    if (idx === 0 || idx === allDates.length - 1 || idx % step === 0) {
      return moment(dStr).format("DD MMM");
    }
    return "";
  });

  return { allDates, labels };
};

const DEFAULT_KPI_CARDS = [
  {
    id: "total-runs",
    title: "Total Runs",
    value: "0",
    iconType: "play",
    colorTheme: "blue",
  },
  {
    id: "success-rate",
    title: "Success Rate",
    value: "0.0%",
    iconType: "check",
    colorTheme: "green",
  },
  {
    id: "avg-duration",
    title: "Avg Duration",
    value: "0s",
    iconType: "clock",
    colorTheme: "purple",
  },
  {
    id: "total-earning",
    title: "Total Earning",
    value: "$0.00",
    iconType: "dollar",
    colorTheme: "cyan",
  },
];

const DEFAULT_RUN_STATUS = {
  title: "Run Status (Last 30 Days)",
  totalRuns: 0,
  breakdown: [
    {
      label: "Successful",
      count: 0,
      percentage: "0.0%",
      color: "#2bb219",
    },
    {
      label: "Failed",
      count: 0,
      percentage: "0.0%",
      color: "#ee0000",
    },
    {
      label: "InProcess",
      count: 0,
      percentage: "0.0%",
      color: "#94a3b8",
    },
  ],
};

const getEmptyUsageTrend = (days = 30) => {
  const enddate = moment().format("YYYY-MM-DD");
  const startdate = moment().subtract(days - 1, "days").format("YYYY-MM-DD");
  const { allDates, labels } = formatTrendDates(startdate, enddate, days);
  return {
    title: `Usage Trend (Last ${days} Days)`,
    labels,
    xAxisTickLabels: labels,
    series: {
      successful: allDates.map(() => 0),
      failed: allDates.map(() => 0),
      inProcess: allDates.map(() => 0),
    },
  };
};

const getEmptyEarningTrend = (days = 30) => {
  const enddate = moment().format("YYYY-MM-DD");
  const startdate = moment().subtract(days - 1, "days").format("YYYY-MM-DD");
  const { allDates, labels } = formatTrendDates(startdate, enddate, days);
  return {
    title: "Earning Trend",
    labels,
    xAxisTickLabels: labels,
    data: allDates.map(() => 0),
    yAxisLabel: "Earning (USD)",
  };
};

const getEmptyResourceUsage = (days = 30) => {
  const enddate = moment().format("YYYY-MM-DD");
  const startdate = moment().subtract(days - 1, "days").format("YYYY-MM-DD");
  const { allDates, labels } = formatTrendDates(startdate, enddate, days);
  return {
    title: `Resource Usage (Last ${days} Days)`,
    filterOptions: ["Last 7 Days", "Last 30 Days", "Last 90 Days"],
    metrics: [
      {
        id: "cpu",
        label: "CPU Usage",
        unit: "%",
        extraLabel: "",
        extraBadgeBg: "#f4f6f8",
        extraBadgeColor: "#4a4a4a",
        avg: "0%",
        max: "0%",
        badgeBg: "rgba(36, 144, 210, 0.12)",
        badgeColor: "#2490d2",
        lineColor: "#2490d2",
        fillColor: "rgba(36, 144, 210, 0.12)",
        maxBadgeBg: "rgba(249, 115, 22, 0.12)",
        maxBadgeColor: "#F97316",
        maxLineColor: "#F97316",
        labels,
        data: allDates.map(() => 0),
        maxData: allDates.map(() => 0),
      },
      {
        id: "memory",
        label: "Memory Usage",
        unit: "%",
        extraLabel: "",
        extraBadgeBg: "#f4f6f8",
        extraBadgeColor: "#4a4a4a",
        avg: "0%",
        max: "0%",
        badgeBg: "rgba(43, 178, 25, 0.12)",
        badgeColor: "#2bb219",
        lineColor: "#2bb219",
        fillColor: "rgba(43, 178, 25, 0.12)",
        maxBadgeBg: "rgba(249, 115, 22, 0.12)",
        maxBadgeColor: "#F97316",
        maxLineColor: "#F97316",
        labels,
        data: allDates.map(() => 0),
        maxData: allDates.map(() => 0),
      },
      {
        id: "network",
        label: "Network Usage",
        unit: "MB",
        avg: "0 MB",
        max: "0 MB",
        badgeBg: "#f3e8ff",
        badgeColor: "#9333ea",
        lineColor: "#9333ea",
        fillColor: "rgba(147, 51, 234, 0.12)",
        maxBadgeBg: "rgba(249, 115, 22, 0.12)",
        maxBadgeColor: "#F97316",
        maxLineColor: "#F97316",
        labels,
        data: allDates.map(() => 0),
        maxData: allDates.map(() => 0),
      },
    ],
  };
};

const Machine360Analytics = () => {
  const { id, pagename } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const queryId = searchParams.get("id");
  const machineParamId = id || queryId;
  const routeStateMachine = location?.state?.machineData;

  const dispatch = useDispatch();
  const [apiMachineData, setApiMachineData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // 1. KPI Cards State & Loading (Defaults to 0s)
  const [kpiCardsState, setKpiCardsState] = useState(DEFAULT_KPI_CARDS);
  const [isKpiLoading, setIsKpiLoading] = useState(false);

  // 2. Run Status State & Loading (Last 30 Days, Defaults to 0s)
  const [runStatusState, setRunStatusState] = useState(DEFAULT_RUN_STATUS);
  const [isRunStatusLoading, setIsRunStatusLoading] = useState(false);

  // 3. Usage Trend State, Selected Days & Loading (7, 30, 90, Defaults to 0s)
  const [usageTrendState, setUsageTrendState] = useState(() => getEmptyUsageTrend(30));
  const [usageDays, setUsageDays] = useState(30);
  const [isUsageTrendLoading, setIsUsageTrendLoading] = useState(false);

  // 4. Earning Trend State, Selected Days & Loading (7, 30, 90, Defaults to 0s)
  const [earningTrendState, setEarningTrendState] = useState(() => getEmptyEarningTrend(30));
  const [earningDays, setEarningDays] = useState(30);
  const [isEarningTrendLoading, setIsEarningTrendLoading] = useState(false);

  // 5. Resource Usage State, Selected Days & Loading (7, 30, 90, Defaults to 0s)
  const [resourceUsageState, setResourceUsageState] = useState(() => getEmptyResourceUsage(30));
  const [resourceDays, setResourceDays] = useState(30);
  const [isResourceLoading, setIsResourceLoading] = useState(false);

  // 6. Machine Versions State & Loading (for version chip dropdown)
  const [versionList, setVersionList] = useState([]);
  const [isVersionLoading, setIsVersionLoading] = useState(false);

  // Dynamic header height measurement for desktop viewport-locked layout
  useEffect(() => {
    const updateHeaderHeight = () => {
      const headerEl = document.querySelector(".header-main");
      if (headerEl) {
        const height = headerEl.getBoundingClientRect().height;
        if (height > 0) {
          document.documentElement.style.setProperty(
            "--m360-header-height",
            `${height}px`
          );
        }
      }
    };

    updateHeaderHeight();

    const headerEl = document.querySelector(".header-main");
    let resizeObserver;
    if (headerEl && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(() => {
        updateHeaderHeight();
      });
      resizeObserver.observe(headerEl);
    }

    window.addEventListener("resize", updateHeaderHeight);
    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener("resize", updateHeaderHeight);
    };
  }, []);

  const isValidId = (val) => val !== null && val !== undefined && val !== "";

  const numericOrStrId = useMemo(() => {
    if (!machineParamId) {
      const refId = Number(routeStateMachine?.refmachineid ?? routeStateMachine?.refMachineId);
      if (!isNaN(refId) && refId > 0) return refId;
      if (isValidId(routeStateMachine?.machineid)) return routeStateMachine.machineid;
      if (isValidId(routeStateMachine?.id)) return routeStateMachine.id;
      if (isValidId(routeStateMachine?.machineId)) return routeStateMachine.machineId;
      return null;
    }
    let targetId = machineParamId;
    try {
      const decrypted = commonservices.bodyDecryption(machineParamId);
      if (isValidId(decrypted)) {
        targetId = decrypted;
      }
    } catch (e) {
      // fallback
    }
    return !isNaN(targetId) && targetId !== "" ? parseInt(targetId, 10) : targetId;
  }, [machineParamId, routeStateMachine]);

  const [selectedMachineId, setSelectedMachineId] = useState(null);
  const activeMachineId = isValidId(selectedMachineId) ? selectedMachineId : numericOrStrId;

  // Keep selectedMachineId in sync when route ID changes
  useEffect(() => {
    if (isValidId(numericOrStrId)) {
      setSelectedMachineId(numericOrStrId);
    }
  }, [numericOrStrId]);

  // Fetch machine versions using root machine ID (refmachineid if child version, else activeMachineId)
  useEffect(() => {
    if (!isValidId(activeMachineId)) return;

    const rootId =
      isValidId(apiMachineData?.dataList1?.[0]?.refmachineid) &&
        apiMachineData.dataList1[0].refmachineid !== 0
        ? apiMachineData.dataList1[0].refmachineid
        : activeMachineId;

    let isMounted = true;
    setIsVersionLoading(true);

    dispatch(GetMachineVersion({ machineid: rootId }))
      .then((res) => {
        if (!isMounted) return;
        if (res?.payload?.isValidate) {
          const rawList =
            res?.payload?.data?.data?.dataList1 ||
            res?.payload?.data?.dataList1 ||
            (Array.isArray(res?.payload?.data) ? res.payload.data : []) ||
            res?.payload?.dataList1 ||
            [];

          if (Array.isArray(rawList) && rawList.length > 0) {
            setVersionList(rawList);
          } else {
            setVersionList([]);
          }
        }
      })
      .catch((err) => {
        console.error("GetMachineVersion failed", err);
      })
      .finally(() => {
        if (isMounted) setIsVersionLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [activeMachineId, apiMachineData?.dataList1?.[0]?.refmachineid, dispatch]);

  const handleVersionChange = (versionItem) => {
    if (!versionItem?.machineid) return;
    const newMachineId = !isNaN(versionItem.machineid)
      ? parseInt(versionItem.machineid, 10)
      : versionItem.machineid;

    if (newMachineId === activeMachineId) return;

    setSelectedMachineId(newMachineId);

    // Update URL so reload / bookmarks retain the selected version
    try {
      const encryptedId = commonservices.bodyEncryption(String(newMachineId));
      const targetUrl = `/marketplace/machine-360/${encryptedId}${pagename ? `/${pagename}` : ""
        }`;
      navigate(targetUrl, { replace: true });
    } catch (err) {
      console.error("Navigation error on version change", err);
    }
  };

  // Call api/v1/Machine/getbyprimarykeyindetails and fetch machine details
  useEffect(() => {
    if (!isValidId(activeMachineId)) {
      setIsLoading(false);
      setApiMachineData(null);
      return;
    }

    let body = {
      id: activeMachineId,
    };

    setIsLoading(true);
    dispatch(GetMachineDetailByPrimaryKey(body))
      .then((res) => {
        const machineDataObj =
          res?.payload?.data?.data || res?.payload?.data;
        const dList1 =
          machineDataObj?.dataList1 || res?.payload?.dataList1;

        if (
          res?.payload?.isValidate &&
          machineDataObj &&
          Array.isArray(dList1) &&
          dList1.length > 0
        ) {
          setApiMachineData(machineDataObj);
        } else {
          setApiMachineData(null);
        }
      })
      .catch((err) => {
        console.error("Machine360Analytics: API fetch failed", err);
        setApiMachineData(null);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [activeMachineId, dispatch]);

  // 1. Bind getallkpidata API
  useEffect(() => {
    if (!isValidId(activeMachineId)) {
      setKpiCardsState(DEFAULT_KPI_CARDS);
      return;
    }
    setIsKpiLoading(true);
    dispatch(GetAllKpiData(activeMachineId))
      .then((res) => {
        if (res?.payload?.isValidate) {
          const kpiRaw =
            res?.payload?.data?.dataList1?.[0] ||
            res?.payload?.data?.data?.dataList1?.[0] ||
            (Array.isArray(res?.payload?.data) ? res.payload.data : []) ||
            res?.payload?.dataList1?.[0];

          if (kpiRaw) {
            setKpiCardsState([
              {
                id: "total-runs",
                title: "Total Runs",
                value:
                  kpiRaw.count !== undefined && kpiRaw.count !== null
                    ? String(kpiRaw.count)
                    : "0",
                iconType: "play",
                colorTheme: "blue",
              },
              {
                id: "success-rate",
                title: "Success Rate",
                value:
                  kpiRaw.success_rate !== undefined && kpiRaw.success_rate !== null
                    ? `${Number(kpiRaw.success_rate).toFixed(1)}%`
                    : "0.0%",
                iconType: "check",
                colorTheme: "green",
              },
              {
                id: "avg-duration",
                title: "Avg Duration",
                value: kpiRaw.avg_runtime || "0s",
                iconType: "clock",
                colorTheme: "purple",
              },
              {
                id: "total-earning",
                title: "Total Earning",
                value:
                  kpiRaw.totalearning !== undefined && kpiRaw.totalearning !== null
                    ? `$${Number(kpiRaw.totalearning).toFixed(2)}`
                    : "$0.00",
                iconType: "dollar",
                colorTheme: "cyan",
              },
            ]);
          } else {
            setKpiCardsState(DEFAULT_KPI_CARDS);
          }
        } else {
          setKpiCardsState(DEFAULT_KPI_CARDS);
        }
      })
      .catch((err) => {
        console.error("GetAllKpiData failed", err);
        setKpiCardsState(DEFAULT_KPI_CARDS);
      })
      .finally(() => {
        setIsKpiLoading(false);
      });
  }, [activeMachineId, dispatch]);

  // 2. Bind getmachinerunstatus API (last 30 days)
  useEffect(() => {
    if (!isValidId(activeMachineId)) {
      setRunStatusState(DEFAULT_RUN_STATUS);
      return;
    }
    setIsRunStatusLoading(true);
    const enddate = moment().format("YYYY-MM-DD");
    const startdate = moment().subtract(29, "days").format("YYYY-MM-DD");

    dispatch(GetMachineRunStatus({ machineid: activeMachineId, startdate, enddate }))
      .then((res) => {
        if (res?.payload?.isValidate) {
          const rows =
            res?.payload?.data?.dataList1 ||
            res?.payload?.data?.data?.dataList1 ||
            (Array.isArray(res?.payload?.data) ? res.payload.data : []) ||
            res?.payload?.dataList1 ||
            [];

          if (Array.isArray(rows) && rows.length > 0) {
            const succRow = rows.find(
              (r) => (r.status || "").toLowerCase() === "successful"
            );
            const failRow = rows.find(
              (r) => (r.status || "").toLowerCase() === "failed"
            );
            const inprRow = rows.find(
              (r) => (r.status || "").toLowerCase() === "inprocess"
            );

            const succ = Number(succRow?.runcount ?? succRow?.runCount ?? 0);
            const fail = Number(failRow?.runcount ?? failRow?.runCount ?? 0);
            const inpr = Number(inprRow?.runcount ?? inprRow?.runCount ?? 0);
            const total = succ + fail + inpr;

            setRunStatusState({
              title: "Run Status (Last 30 Days)",
              totalRuns: total,
              breakdown: [
                {
                  label: "Successful",
                  count: succ,
                  percentage:
                    total > 0 ? `${((succ / total) * 100).toFixed(1)}%` : "0.0%",
                  color: "#2bb219",
                },
                {
                  label: "Failed",
                  count: fail,
                  percentage:
                    total > 0 ? `${((fail / total) * 100).toFixed(1)}%` : "0.0%",
                  color: "#ee0000",
                },
                {
                  label: "InProcess",
                  count: inpr,
                  percentage:
                    total > 0 ? `${((inpr / total) * 100).toFixed(1)}%` : "0.0%",
                  color: "#94a3b8",
                },
              ],
            });
          } else {
            setRunStatusState(DEFAULT_RUN_STATUS);
          }
        } else {
          setRunStatusState(DEFAULT_RUN_STATUS);
        }
      })
      .catch((err) => {
        console.error("GetMachineRunStatus failed", err);
        setRunStatusState(DEFAULT_RUN_STATUS);
      })
      .finally(() => {
        setIsRunStatusLoading(false);
      });
  }, [activeMachineId, dispatch]);

  // 3. Bind getmachineusagetrend API (7, 30, 90 days)
  const fetchUsageTrend = (days = 30, targetMachineId = activeMachineId) => {
    if (!isValidId(targetMachineId)) {
      setUsageDays(days);
      setUsageTrendState(getEmptyUsageTrend(days));
      return;
    }
    setUsageDays(days);
    setIsUsageTrendLoading(true);
    const enddate = moment().format("YYYY-MM-DD");
    const startdate = moment().subtract(days - 1, "days").format("YYYY-MM-DD");

    dispatch(GetMachineUsageTrend({ machineid: targetMachineId, startdate, enddate }))
      .then((res) => {
        if (res?.payload?.isValidate) {
          const rows =
            res?.payload?.data?.dataList1 ||
            res?.payload?.data?.data?.dataList1 ||
            (Array.isArray(res?.payload?.data) ? res.payload.data : []) ||
            res?.payload?.dataList1 ||
            [];

          if (Array.isArray(rows) && rows.length > 0) {
            const { allDates, labels } = formatTrendDates(startdate, enddate, days);
            const succSeries = [];
            const failSeries = [];
            const inprSeries = [];

            allDates.forEach((dStr) => {
              const succItem = rows.find(
                (r) =>
                  moment(r.starton).format("YYYY-MM-DD") === dStr &&
                  (r.status || "").toLowerCase() === "successful"
              );
              const failItem = rows.find(
                (r) =>
                  moment(r.starton).format("YYYY-MM-DD") === dStr &&
                  (r.status || "").toLowerCase() === "failed"
              );
              const inprItem = rows.find(
                (r) =>
                  moment(r.starton).format("YYYY-MM-DD") === dStr &&
                  (r.status || "").toLowerCase() === "inprocess"
              );

              succSeries.push(Number(succItem?.runcount ?? succItem?.runCount ?? 0));
              failSeries.push(Number(failItem?.runcount ?? failItem?.runCount ?? 0));
              inprSeries.push(Number(inprItem?.runcount ?? inprItem?.runCount ?? 0));
            });

            setUsageTrendState({
              title: `Usage Trend (Last ${days} Days)`,
              labels: labels,
              xAxisTickLabels: labels,
              series: {
                successful: succSeries,
                failed: failSeries,
                inProcess: inprSeries,
              },
            });
          } else {
            setUsageTrendState(getEmptyUsageTrend(days));
          }
        } else {
          setUsageTrendState(getEmptyUsageTrend(days));
        }
      })
      .catch((err) => {
        console.error("GetMachineUsageTrend failed", err);
        setUsageTrendState(getEmptyUsageTrend(days));
      })
      .finally(() => {
        setIsUsageTrendLoading(false);
      });
  };

  useEffect(() => {
    fetchUsageTrend(usageDays, activeMachineId);
  }, [activeMachineId]);

  // 4. Bind getmachineearningtrend API (7, 30, 90 days, titled "Earning Trend")
  const fetchEarningTrend = (days = 30, targetMachineId = activeMachineId) => {
    if (!isValidId(targetMachineId)) {
      setEarningDays(days);
      setEarningTrendState(getEmptyEarningTrend(days));
      return;
    }
    setEarningDays(days);
    setIsEarningTrendLoading(true);
    const enddate = moment().format("YYYY-MM-DD");
    const startdate = moment().subtract(days - 1, "days").format("YYYY-MM-DD");

    dispatch(GetMachineEarningTrend({ machineid: targetMachineId, startdate, enddate }))
      .then((res) => {
        if (res?.payload?.isValidate) {
          const rows =
            res?.payload?.data?.dataList1 ||
            res?.payload?.data?.data?.dataList1 ||
            (Array.isArray(res?.payload?.data) ? res.payload.data : []) ||
            res?.payload?.dataList1 ||
            [];

          if (Array.isArray(rows) && rows.length > 0) {
            const { allDates, labels } = formatTrendDates(startdate, enddate, days);
            const earningSeries = [];

            allDates.forEach((dStr) => {
              const sum = rows
                .filter(
                  (r) =>
                    moment(r.entrydate || r.entryDate).format("YYYY-MM-DD") === dStr
                )
                .reduce((acc, r) => acc + Number(r.earning || 0), 0);
              earningSeries.push(Number(sum.toFixed(2)));
            });

            setEarningTrendState({
              title: "Earning Trend",
              labels: labels,
              xAxisTickLabels: labels,
              data: earningSeries,
              yAxisLabel: "Earning (USD)",
            });
          } else {
            setEarningTrendState(getEmptyEarningTrend(days));
          }
        } else {
          setEarningTrendState(getEmptyEarningTrend(days));
        }
      })
      .catch((err) => {
        console.error("GetMachineEarningTrend failed", err);
        setEarningTrendState(getEmptyEarningTrend(days));
      })
      .finally(() => {
        setIsEarningTrendLoading(false);
      });
  };

  useEffect(() => {
    fetchEarningTrend(earningDays, activeMachineId);
  }, [activeMachineId]);

  // 5. Bind getmachineresourceusage API (7, 30, 90 days)
  const fetchResourceUsage = (days = 30, targetMachineId = activeMachineId) => {
    if (!isValidId(targetMachineId)) {
      setResourceDays(days);
      setResourceUsageState(getEmptyResourceUsage(days));
      return;
    }
    setResourceDays(days);
    setIsResourceLoading(true);
    const enddate = moment().format("YYYY-MM-DD");
    const startdate = moment().subtract(days - 1, "days").format("YYYY-MM-DD");

    dispatch(GetMachineResourceUsage({ machineid: targetMachineId, startdate, enddate }))
      .then((res) => {
        if (res?.payload?.isValidate) {
          const rows =
            res?.payload?.data?.dataList1 ||
            res?.payload?.data?.data?.dataList1 ||
            (Array.isArray(res?.payload?.data) ? res.payload.data : []) ||
            res?.payload?.dataList1 ||
            [];

          if (Array.isArray(rows) && rows.length > 0) {
            const { allDates, labels } = formatTrendDates(startdate, enddate, days);
            const cpuData = [];
            const maxCpuData = [];
            const memData = [];
            const maxMemData = [];
            const networkData = [];
            const maxNetworkData = [];

            const getRowDate = (r) => {
              const raw = r.createdon || r.createdOn || r.entrydate || r.entryDate;
              if (!raw) return "";
              if (typeof raw === "string" && raw.length >= 10) {
                return raw.slice(0, 10);
              }
              return moment(raw).format("YYYY-MM-DD");
            };

            allDates.forEach((dStr) => {
              const item = rows.find((r) => getRowDate(r) === dStr);
              cpuData.push(
                item
                  ? Number(Number(item.cpuusage ?? item.cpu_usage ?? 0).toFixed(1))
                  : 0
              );
              maxCpuData.push(
                item
                  ? Number(
                    Number(
                      item.maxcpuusage ??
                      item.max_cpu_usage ??
                      item.maxCpuUsage ??
                      item.cpuusage ??
                      item.cpu_usage ??
                      0
                    ).toFixed(1)
                  )
                  : 0
              );
              memData.push(
                item
                  ? Number(Number(item.memoryusage ?? item.memory_usage_per ?? 0).toFixed(1))
                  : 0
              );
              maxMemData.push(
                item
                  ? Number(
                    Number(
                      item.maxmemoryusage ??
                      item.max_memory_usage ??
                      item.maxMemoryUsage ??
                      item.max_memory_usage_per ??
                      item.memoryusage ??
                      item.memory_usage_per ??
                      0
                    ).toFixed(1)
                  )
                  : 0
              );
              networkData.push(
                item
                  ? Number(
                    Number(
                      item.networkusage ??
                      item.network_usage ??
                      item.diskusage ??
                      item.disk_usage ??
                      0
                    ).toFixed(1)
                  )
                  : 0
              );
              maxNetworkData.push(
                item
                  ? Number(
                    Number(
                      item.maxnetworkusage ??
                      item.max_network_usage ??
                      item.maxNetworkUsage ??
                      item.networkusage ??
                      item.network_usage ??
                      0
                    ).toFixed(1)
                  )
                  : 0
              );
            });

            // Filter rows to only those within the selected date range (last 7, 30, or 90 days)
            const periodRows = rows.filter((r) => allDates.includes(getRowDate(r)));

            // Divisor is strictly the selected period days (7 for 7 days, 30 for 30 days, 90 for 90 days)
            const count = Number(days) || allDates.length || 1;

            const avgCpu = (
              periodRows.reduce(
                (sum, r) => sum + Number(r.cpuusage ?? r.cpu_usage ?? 0),
                0
              ) / count
            ).toFixed(0);

            const avgMem = (
              periodRows.reduce(
                (sum, r) =>
                  sum + Number(r.memoryusage ?? r.memory_usage_per ?? 0),
                0
              ) / count
            ).toFixed(0);

            const avgNetwork = (
              periodRows.reduce(
                (sum, r) =>
                  sum +
                  Number(
                    r.networkusage ??
                    r.network_usage ??
                    r.diskusage ??
                    r.disk_usage ??
                    0
                  ),
                0
              ) / count
            ).toFixed(1);

            // Peak (Max) values strictly for the selected period
            const maxCpuVal = Math.max(
              ...(periodRows.length > 0
                ? periodRows.map((r) =>
                  Number(
                    r.maxcpuusage ??
                    r.max_cpu_usage ??
                    r.maxCpuUsage ??
                    r.cpuusage ??
                    r.cpu_usage ??
                    0
                  )
                )
                : maxCpuData),
              0
            ).toFixed(0);

            const maxMemVal = Math.max(
              ...(periodRows.length > 0
                ? periodRows.map((r) =>
                  Number(
                    r.maxmemoryusage ??
                    r.max_memory_usage ??
                    r.maxMemoryUsage ??
                    r.max_memory_usage_per ??
                    r.memoryusage ??
                    r.memory_usage_per ??
                    0
                  )
                )
                : maxMemData),
              0
            ).toFixed(0);

            const maxNetworkVal = Math.max(
              ...(periodRows.length > 0
                ? periodRows.map((r) =>
                  Number(
                    r.maxnetworkusage ??
                    r.max_network_usage ??
                    r.maxNetworkUsage ??
                    r.networkusage ??
                    r.network_usage ??
                    0
                  )
                )
                : maxNetworkData),
              0
            ).toFixed(1);

            const rowWithAvg =
              (Array.isArray(rows) &&
                rows.find(
                  (r) =>
                    (r?.avgcpu !== undefined && r?.avgcpu !== null && r?.avgcpu !== "") ||
                    (r?.avgram !== undefined && r?.avgram !== null && r?.avgram !== "")
                )) ||
              (Array.isArray(rows) && rows.length > 0 ? rows[0] : {});

            const summaryList =
              res?.payload?.data?.dataList2 ||
              res?.payload?.data?.data?.dataList2 ||
              res?.payload?.dataList2 ||
              [];
            const summary =
              Array.isArray(summaryList) && summaryList.length > 0
                ? summaryList[0]
                : {};

            const rawAvgCpu =
              rowWithAvg?.avgcpu ??
              rowWithAvg?.avgCpu ??
              rowWithAvg?.avg_cpu ??
              summary?.avgcpu ??
              summary?.avgCpu ??
              summary?.avg_cpu;
            let cpuSummaryLabel = "";
            if (rawAvgCpu !== undefined && rawAvgCpu !== null && rawAvgCpu !== "") {
              const num = Number(rawAvgCpu);
              const val = !isNaN(num)
                ? `${Number(num.toFixed(2))} CORE`
                : String(rawAvgCpu);
              cpuSummaryLabel = `Avg Set: ${val}`;
            }

            const rawAvgRam =
              rowWithAvg?.avgram ??
              rowWithAvg?.avgRam ??
              rowWithAvg?.avg_ram ??
              summary?.avgram ??
              summary?.avgRam ??
              summary?.avg_ram;
            let ramSummaryLabel = "";
            if (rawAvgRam !== undefined && rawAvgRam !== null && rawAvgRam !== "") {
              const num = Number(rawAvgRam);
              const inGb = num / 1024;
              const val = !isNaN(num)
                ? `${Number(inGb.toFixed(2)).toLocaleString()} GB`
                : String(rawAvgRam);
              ramSummaryLabel = `Avg Set: ${val}`;
            }

            setResourceUsageState({
              title: `Resource Usage (Last ${days} Days)`,
              filterOptions: ["Last 7 Days", "Last 30 Days", "Last 90 Days"],
              metrics: [
                {
                  id: "cpu",
                  label: "CPU Usage",
                  unit: "%",
                  extraLabel: cpuSummaryLabel,
                  extraBadgeBg: "#f4f6f8",
                  extraBadgeColor: "#4a4a4a",
                  avg: `${avgCpu}%`,
                  max: `${maxCpuVal}%`,
                  badgeBg: "rgba(36, 144, 210, 0.12)",
                  badgeColor: "#2490d2",
                  lineColor: "#2490d2",
                  fillColor: "rgba(36, 144, 210, 0.12)",
                  maxBadgeBg: "rgba(249, 115, 22, 0.12)",
                  maxBadgeColor: "#F97316",
                  maxLineColor: "#F97316",
                  labels: labels,
                  data: cpuData,
                  maxData: maxCpuData,
                },
                {
                  id: "memory",
                  label: "Memory Usage",
                  unit: "%",
                  extraLabel: ramSummaryLabel,
                  extraBadgeBg: "#f4f6f8",
                  extraBadgeColor: "#4a4a4a",
                  avg: `${avgMem}%`,
                  max: `${maxMemVal}%`,
                  badgeBg: "rgba(43, 178, 25, 0.12)",
                  badgeColor: "#2bb219",
                  lineColor: "#2bb219",
                  fillColor: "rgba(43, 178, 25, 0.12)",
                  maxBadgeBg: "rgba(249, 115, 22, 0.12)",
                  maxBadgeColor: "#F97316",
                  maxLineColor: "#F97316",
                  labels: labels,
                  data: memData,
                  maxData: maxMemData,
                },
                {
                  id: "network",
                  label: "Network Usage",
                  unit: "MB",
                  avg: `${Number(avgNetwork).toLocaleString(undefined, {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                  })} MB`,
                  max: `${Number(maxNetworkVal).toLocaleString(undefined, {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                  })} MB`,
                  badgeBg: "#f3e8ff",
                  badgeColor: "#9333ea",
                  lineColor: "#9333ea",
                  fillColor: "rgba(147, 51, 234, 0.12)",
                  maxBadgeBg: "rgba(249, 115, 22, 0.12)",
                  maxBadgeColor: "#F97316",
                  maxLineColor: "#F97316",
                  labels: labels,
                  data: networkData,
                  maxData: maxNetworkData,
                },
              ],
            });
          } else {
            setResourceUsageState(getEmptyResourceUsage(days));
          }
        } else {
          setResourceUsageState(getEmptyResourceUsage(days));
        }
      })
      .catch((err) => {
        console.error("GetMachineResourceUsage failed", err);
        setResourceUsageState(getEmptyResourceUsage(days));
      })
      .finally(() => {
        setIsResourceLoading(false);
      });
  };

  useEffect(() => {
    fetchResourceUsage(resourceDays, activeMachineId);
  }, [activeMachineId]);

  const forceLoading =
    searchParams.get("loading") === "true" ||
    searchParams.get("skeleton") === "true";
  const displayLoading = isLoading || forceLoading;

  // Determine if valid machine data was found from API or navigation state
  const hasMachineData = Boolean(
    (apiMachineData?.dataList1 && apiMachineData.dataList1.length > 0) ||
    (routeStateMachine && (routeStateMachine.machinename || routeStateMachine.machineid))
  );

  // Bind dynamic data from API, fallback to route-passed machine state, then static mock data
  const { headerData, sidebarData } = useMemo(() => {
    if (!hasMachineData) {
      return { headerData: null, sidebarData: null };
    }

    const dList1 = apiMachineData?.dataList1?.[0];
    const dList2 = apiMachineData?.dataList2?.[0];

    // Find active version in version list to get its status
    const matchedVersion = versionList?.find(
      (v) => String(v.machineid) === String(activeMachineId)
    );

    const machineStatus =
      dList1?.status ||
      dList1?.machinestatus ||
      matchedVersion?.status ||
      matchedVersion?.machinestatus ||
      routeStateMachine?.status ||
      routeStateMachine?.machinestatus ||
      "";

    // Header Data
    const header = {
      ...mockMachine360Data.header,
      ...(isValidId(activeMachineId) ? { id: activeMachineId } : {}),
      ...(pagename ? { pageName: pagename } : {}),
      status: machineStatus || "Publish",
      name:
        dList1?.machinename ||
        routeStateMachine?.machinename ||
        mockMachine360Data.header.name,
      logo: dList1?.machinelogo || routeStateMachine?.machinelogo || null,
      version:
        dList1?.versionnumber ||
        dList1?.lastversion ||
        routeStateMachine?.versionnumber ||
        mockMachine360Data.header.version,
      rating:
        dList2?.averagerating !== undefined && dList2?.averagerating !== null
          ? Number(dList2.averagerating)
          : routeStateMachine?.avgrating !== undefined && routeStateMachine?.avgrating !== null
            ? Number(routeStateMachine.avgrating)
            : mockMachine360Data.header.rating,
      description: dList2?.shortdescription
        ? commonservices.stripHTML(dList2.shortdescription)
        : routeStateMachine?.shortdescription
          ? commonservices.stripHTML(routeStateMachine.shortdescription)
          : mockMachine360Data.header.description,
      pricing:
        dList1?.price !== undefined
          ? Number(dList1.price) > 0
            ? `$${Number(dList1.price).toFixed(2)}`
            : "Free"
          : routeStateMachine?.price !== undefined
            ? Number(routeStateMachine.price) > 0
              ? `$${Number(routeStateMachine.price).toFixed(2)}`
              : "Free"
            : mockMachine360Data.header.pricing,
      tags: dList1?.categoryname
        ? [
          dList1.categoryname,
          ...(dList1.industryname ? [dList1.industryname] : []),
          ...(mockMachine360Data.header.tags.filter(
            (t) => t !== dList1.categoryname && t !== dList1.industryname
          ).slice(0, 2)),
        ]
        : routeStateMachine?.categoryname
          ? [
            routeStateMachine.categoryname,
            ...(routeStateMachine?.industryname ? [routeStateMachine.industryname] : []),
          ]
          : mockMachine360Data.header.tags,
      developer: {
        name:
          dList1?.subscribername ||
          dList1?.authorname ||
          routeStateMachine?.subscribername ||
          routeStateMachine?.authorname ||
          mockMachine360Data.header.developer.name,
        contactEmail:
          dList1?.supportemail ||
          dList2?.supportemail ||
          routeStateMachine?.supportemail ||
          mockMachine360Data.header.developer.contactEmail,
        lastUpdated: dList1?.lastupdatedon
          ? moment(dList1.lastupdatedon).format("MMM DD, YYYY")
          : routeStateMachine?.lastupdatedon
            ? moment(routeStateMachine.lastupdatedon).format("MMM DD, YYYY")
            : mockMachine360Data.header.developer.lastUpdated,
        version:
          dList1?.versionnumber ||
          dList1?.lastversion ||
          routeStateMachine?.versionnumber ||
          mockMachine360Data.header.developer.version,
      },
    };

    // Sidebar Data
    const sidebar = {
      ...mockMachine360Data.sidebar,
      classification: {
        industry:
          dList1?.industryname ||
          routeStateMachine?.industryname ||
          mockMachine360Data.sidebar.classification.industry,
        industryLink: mockMachine360Data.sidebar.classification.industryLink,
      },
      categories: dList1?.categoryname
        ? [dList1.categoryname]
        : routeStateMachine?.categoryname
          ? [routeStateMachine.categoryname]
          : mockMachine360Data.sidebar.categories,
      subCategories:
        dList1?.subcategoryname ||
        routeStateMachine?.subcategoryname ||
        mockMachine360Data.sidebar.subCategories,
      compatibility: {
        dependentMachines:
          dList2?.dependentmachines ||
          mockMachine360Data.sidebar.compatibility.dependentMachines,
        compatibleMachines:
          dList2?.compatiblemachines ||
          mockMachine360Data.sidebar.compatibility.compatibleMachines,
        version:
          dList1?.versionnumber ||
          dList1?.lastversion ||
          routeStateMachine?.versionnumber ||
          mockMachine360Data.sidebar.compatibility.version,
      },
      dataFiles: [
        {
          id: "input-data",
          title: "Input Data",
          description: dList2?.inputdescription
            ? commonservices.stripHTML(dList2.inputdescription)
            : mockMachine360Data.sidebar.dataFiles[0].description,
          downloadUrl:
            dList1?.inputsamplefileurl ||
            mockMachine360Data.sidebar.dataFiles[0].downloadUrl,
        },
        {
          id: "output-data",
          title: "Output Data",
          description: dList2?.outputdescription
            ? commonservices.stripHTML(dList2.outputdescription)
            : mockMachine360Data.sidebar.dataFiles[1].description,
          downloadUrl:
            dList1?.outputsamplefileurl ||
            mockMachine360Data.sidebar.dataFiles[1].downloadUrl,
        },
      ],
      infrastructure: {
        osRequirement:
          dList2?.osrequirmet ||
          mockMachine360Data.sidebar.infrastructure.osRequirement,
        cpu: dList2?.cpu || mockMachine360Data.sidebar.infrastructure.cpu,
        memory:
          dList2?.ram ||
          dList2?.storage ||
          mockMachine360Data.sidebar.infrastructure.memory,
        cloud: dList2?.cloud || mockMachine360Data.sidebar.infrastructure.cloud,
      },
      usageStats: {
        totalOnboarded:
          dList2?.totlworkflows !== undefined
            ? dList2.totlworkflows
            : mockMachine360Data.sidebar.usageStats.totalOnboarded,
        activeOnBoarding:
          dList2?.activeworkflows !== undefined
            ? dList2.activeworkflows
            : mockMachine360Data.sidebar.usageStats.activeOnBoarding,
      },
    };

    return { headerData: header, sidebarData: sidebar };
  }, [hasMachineData, apiMachineData, activeMachineId, pagename, versionList, routeStateMachine]);

  return (
    <div className="dashboard-main market-place-page-main m360-dashboard-root">
      {/* Top Hero Banner Section with Skyline Background matching /dashboard */}
      <section className="m360-hero-banner">
        <div className="m360-container">
          <Machine360Header
            headerData={hasMachineData ? headerData : null}
            isLoading={displayLoading}
            pageName={pagename}
          />
        </div>
      </section>

      {/* Main Content Area */}
      <main className="m360-container m360-main-content">
        {displayLoading ? (
          <div className="m360-main-layout">
            {/* Left Column: Sidebar skeleton */}
            <Machine360Sidebar
              sidebarData={sidebarData}
              isLoading={true}
              versionList={versionList}
              activeMachineId={activeMachineId}
              currentVersionNumber="1.0.0"
              isVersionLoading={isVersionLoading}
              onVersionChange={handleVersionChange}
            />

            {/* Right Column: Analytics Dashboard skeleton */}
            <div className="m360-analytics-area">
              {/* Row 1: 4 Top KPI Cards (getallkpidata) */}
              <Machine360KpiCards
                kpiCards={kpiCardsState}
                isLoading={true}
              />

              {/* Row 2: Run Status & Usage Trend */}
              <div className="m360-charts-row-equal">
                <RunStatusChart
                  data={runStatusState}
                  isLoading={true}
                />
                <UsageTrendChart
                  data={usageTrendState}
                  selectedDays={usageDays}
                  onPeriodChange={fetchUsageTrend}
                  isLoading={true}
                />
              </div>

              {/* Row 3: Full Width Earning Trend Chart */}
              <CostTrendChart
                data={earningTrendState}
                selectedDays={earningDays}
                onPeriodChange={fetchEarningTrend}
                isLoading={true}
              />

              {/* Row 4: Resource Usage Spanning Full Width Horizontally */}
              <div className="m360-resource-usage-row">
                <ResourceUsageSection
                  data={resourceUsageState}
                  selectedDays={resourceDays}
                  onPeriodChange={fetchResourceUsage}
                  isLoading={true}
                />
              </div>
            </div>
          </div>
        ) : !hasMachineData ? (
          <div className="m360-no-data-card">
            <Nodata />
          </div>
        ) : (
          <div className="m360-main-layout">
            {/* Left Column: Sidebar */}
            <Machine360Sidebar
              sidebarData={sidebarData}
              isLoading={false}
              versionList={versionList}
              activeMachineId={activeMachineId}
              currentVersionNumber={
                apiMachineData?.dataList1?.[0]?.versionnumber ||
                apiMachineData?.dataList1?.[0]?.lastversion ||
                sidebarData?.compatibility?.version ||
                "1.0.0"
              }
              isVersionLoading={isVersionLoading}
              onVersionChange={handleVersionChange}
            />

            {/* Right Column: Analytics Dashboard */}
            <div className="m360-analytics-area">
              {/* Row 1: 4 Top KPI Cards (getallkpidata) */}
              <Machine360KpiCards
                kpiCards={kpiCardsState}
                isLoading={isKpiLoading}
              />

              {/* Row 2: Run Status (getmachinerunstatus, last 30 days) & Usage Trend (getmachineusagetrend, 7/30/90 days) */}
              <div className="m360-charts-row-equal">
                <RunStatusChart
                  data={runStatusState}
                  isLoading={isRunStatusLoading}
                />
                <UsageTrendChart
                  data={usageTrendState}
                  selectedDays={usageDays}
                  onPeriodChange={fetchUsageTrend}
                  isLoading={isUsageTrendLoading}
                />
              </div>

              {/* Row 3: Full Width Earning Trend Chart (getmachineearningtrend, 7/30/90 days) */}
              <CostTrendChart
                data={earningTrendState}
                selectedDays={earningDays}
                onPeriodChange={fetchEarningTrend}
                isLoading={isEarningTrendLoading}
              />

              {/* Row 4: Resource Usage Spanning Full Width Horizontally */}
              <div className="m360-resource-usage-row">
                <ResourceUsageSection
                  data={resourceUsageState}
                  selectedDays={resourceDays}
                  onPeriodChange={fetchResourceUsage}
                  isLoading={isResourceLoading}
                />
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Machine360Analytics;
